import { observer } from 'mobx-react';
import { FC, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useCheckoutStoreContext } from '../../context';
import { DELIVERY_TYPES } from 'src/constants/deliveryTypes';
import { PAYMENT_TYPES } from 'src/constants/main';
import { ORDER_STATUS } from 'src/constants/order';
import { appStore } from 'src/mobx/appStore';
import { localeStore } from 'src/mobx/localesStore';
import { formatPrice } from 'src/utils/formatter';
import { getPaymentMethodIcon, getWalletCurrency } from 'src/utils/helpers';
import { Collapsible } from 'src/view/components/Collapsible/Collapsible';
import Icon from 'src/view/components/Controls/Icon/Icon';
import Text from '~/view/components/Text/Text';
import './style.scss';
import { TOOLTIP_DURATION, TOOLTIP_POSITION } from '~/view/components/Controls/CustomTooltip/CustomTooltip';
import { PROMOCODE_PROVIDE_AS } from '~/view/screens/MarketingScreen/screens/PromocodesScreen/types/promocodes';

const OrderCartTotalInfo: FC = observer((): JSX.Element => {
  const { store: checkoutStore } = useCheckoutStoreContext();

  const showDeliveryFee = useMemo(
    () => checkoutStore.params.delivery_type === DELIVERY_TYPES.DELIVERY,
    [checkoutStore.params.delivery_type, checkoutStore.params.payment_type]
  );

  /**
   * Calculator total
   *
   * @type {number | undefined}
   */
  const total = useMemo(
    () =>
      checkoutStore.calculator?.promocode
        ? checkoutStore.calculator?.discounted_total ?? checkoutStore.calculator?.total
        : checkoutStore.calculator?.total,
    [checkoutStore.calculator]
  );

  const vat_percent = useMemo(() => checkoutStore.dependencies.branch.vat_percent ?? appStore.settings?.vat, [checkoutStore.dependencies.branch]);

  const refundSum = useMemo((): number | null => {
    const { dependencies, calculator } = checkoutStore;
    if (!dependencies.order || dependencies.order?.order_status_code === ORDER_STATUS.DRAFT || calculator.loading) return null;
    return checkoutStore.refundPrice();
  }, [checkoutStore.dependencies.order, checkoutStore.calculator]);

  return (
    <>
      <div className="order-total bg-graysoft">
        {/* Donation Subtotal */}
        {checkoutStore.calculator?.donation_subtotal ? (
          <div className="order-total__container order-total__row">
            <p className="color-dark text-size-16">{localeStore.t('donation')}</p>
            <p className="family-regular color-dark text-size-14">
              {checkoutStore.calculator ? checkoutStore.formatPrice(checkoutStore.calculator.donation_subtotal) : <Skeleton width={60} />}
            </p>
          </div>
        ) : null}

        {/* Total amount excl. VAT */}
        {checkoutStore.calculator?.subtotal ? (
          <div className="order-total__container order-total__row">
            <p className="family-medium color-dark text-size-16">{localeStore.t('total_amount_excl_vat')}</p>
            <p className="family-regular color-dark text-size-14">
              {checkoutStore.calculator ? checkoutStore.formatPrice(checkoutStore.calculator.subtotal) : <Skeleton width={60} />}
            </p>
          </div>
        ) : null}

        {/* Discount */}
        {checkoutStore.calculator?.promocode ? (
          <Collapsible
            className="order-total__container"
            label={localeStore.t('discount')}
            headerRight={
              <p className="family-regular color-dark text-size-14">
                <p className="family-regular color-dark text-size-14">{checkoutStore.formatPrice(-checkoutStore.calculator?.discount_amount)}</p>
              </p>
            }
            collpsedContent={
              /* Discount description */
              <Text
                type="custom"
                className="color-dark text-size-12"
                text={checkoutStore.calculator.promocode}
                tooltip={
                  checkoutStore.calculator?.discount_description
                    ? {
                        message: checkoutStore.calculator?.discount_description,
                        duration: TOOLTIP_DURATION.EXTRA_LARGE,
                        position: TOOLTIP_POSITION.PORTAL,
                        iconSize: 16,
                      }
                    : null
                }
              />
            }
          />
        ) : null}

        {/* VAT % */}
        <Collapsible
          className="order-total__container"
          label={`${localeStore.t('vat')} ${vat_percent ? ` ${vat_percent}%` : ''}`}
          headerRight={<p className="family-regular color-dark text-size-14">{checkoutStore.formatPrice(checkoutStore.calculator?.vat)}</p>}
          collpsedContent={
            /* Total amount incl Vat */
            <div className="order-total__row">
              <p className="color-dark text-size-12">{localeStore.t('total_amount_incl_vat')}</p>
              <p className="color-dark text-size-12">
                {checkoutStore.calculator ? (
                  checkoutStore.formatPrice(checkoutStore.calculator.subtotal + checkoutStore.calculator?.vat)
                ) : (
                  <Skeleton width={60} />
                )}
              </p>
            </div>
          }
        />

        {/* Delivery fee */}
        {showDeliveryFee ? (
          <Collapsible
            className="order-total__container"
            label={localeStore.t('Delivery Fee')}
            headerRight={
              checkoutStore.calculator ? (
                checkoutStore.formatPrice(checkoutStore.calculator.delivery_fee + checkoutStore.calculator.taxable_delivery_fee)
              ) : (
                <Skeleton width={60} />
              )
            }
            collpsedContent={
              checkoutStore.calculator.taxable_delivery_fee ? (
                <div className="d-flex flex-column full-width">
                  {/* Delivery fee excluding Vat */}
                  <div className="order-total__row">
                    <p className="color-dark text-size-12">{localeStore.t('delivery_excluding_vat')}</p>
                    <p className="color-dark text-size-12">
                      {checkoutStore.calculator ? checkoutStore.formatPrice(checkoutStore.calculator.delivery_fee) : <Skeleton width={60} />}
                    </p>
                  </div>

                  {/* Delivery fee Vat */}
                  <div className="order-total__row">
                    <p className="color-dark text-size-12">{localeStore.t('delivery_fee_vat')}</p>
                    <p className="color-dark text-size-12">{checkoutStore.formatPrice(checkoutStore.calculator.taxable_delivery_fee)}</p>
                  </div>
                </div>
              ) : null
            }
          />
        ) : null}

        {/* Wallet Discount */}
        {checkoutStore.calculator && checkoutStore.calculator.wallet_discount ? (
          <div className="order-total__container order-total__row">
            <p className="color-dark text-size-16 mr-3">{localeStore.t('wallet_discount')}</p>
            <p className="family-regular color-dark text-size-14 text-uppercase ">
              {formatPrice(-checkoutStore.calculator.wallet_discount, getWalletCurrency())}
            </p>
          </div>
        ) : null}

        <div className="order-total__container d-flex flex-column">
          {/* Total */}
          <div className="order-total__row">
            <p className="family-bold color-dark text-size-18">{localeStore.t('txt_total')}</p>
            <p className="family-semibold color-dark text-size-14">
              {checkoutStore.calculator ? checkoutStore.formatPrice(total) : <Skeleton width={60} />}
            </p>
          </div>

          {/* Payment method */}
          <div className="order-total__row">
            <p className="color-dark text-size-12 mr-3">{localeStore.t('payment_method')}</p>
            <div className="d-flex align-items-center">
              <div className="mr-2">
                {checkoutStore.calculator ? (
                  <Icon name={getPaymentMethodIcon(checkoutStore.params.payment_type)} size={24} />
                ) : (
                  <Skeleton width={14} />
                )}
              </div>
              <p className="family-regular color-dark text-size-14">
                {checkoutStore.calculator ? localeStore.t(PAYMENT_TYPES[checkoutStore.params.payment_type]) : <Skeleton width={60} />}
              </p>
            </div>
          </div>

          {/* Cashback */}
          {checkoutStore.dependencies.branch?.is_cashback && checkoutStore.calculator.cashback?.name !== null ? (
            checkoutStore.calculator?.promo_code && checkoutStore.calculator.promo_code.provide_as === PROMOCODE_PROVIDE_AS.CASHBACK ? (
              <Collapsible
                className="order-total__collapsible_row"
                label={localeStore.t('cashback')}
                headerRight={
                  <p className="family-regular color-dark text-size-14">{checkoutStore.formatPrice(checkoutStore.calculator.cashback?.amount)}</p>
                }
                collpsedContent={<p className="color-dark text-size-12">{checkoutStore.calculator.promo_code?.name}</p>}
              />
            ) : (
              <div className="order-total__row">
                <p className="color-dark text-size-12">{localeStore.t('cashback')}</p>
                <p className="family-regular color-dark text-size-12">{checkoutStore.formatPrice(checkoutStore.calculator.cashback?.amount)}</p>
              </div>
            )
          ) : null}

          {/* Refund */}
          {refundSum && refundSum > 0 ? (
            <div className="order-total__row color-red">
              <p className="family-medium text-size-12">{localeStore.t('refund')}</p>
              <p className="text-size-12 text-uppercase">{checkoutStore.formatPrice(refundSum)}</p>
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
});

export default OrderCartTotalInfo;
